import React, { useEffect, useRef, useState } from 'react'
import { FleetSection, Notice } from '../../../../../shared/sso-workspace/fleet-page'
import { serverMessage } from '../../../../../shared/error/server-message'
import { ErrorReference } from '../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../shared/error/use-error-line'
import { deleteDevice, getCapabilities, isOffline, listDevices, registerDevice } from '../../../../../api-new/state/notify'
import { useSelfNotifyAccess } from '../../../../../shared/notify/self-access'
import { SW_PATH, browserLabel, pushSupported, rememberDevice, rememberedDevice, subscriptionBody, urlBase64ToUint8Array } from './push-support'

const BLOCKED = "You've blocked notifications for this site. To get them, allow notifications for this site in your browser's settings, then come back here."
const FAILED = "Push couldn't be switched on. Please try again."
const OFF_FAILED = "Push couldn't be switched off. Please try again."

const zone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
  } catch (e) {
    return undefined
  }
}

// Web push for this browser (plan P13): explain first, then ask; show the
// state; and a way to turn it off. Shown only where it can work: the
// browser has Notification, PushManager and service workers (feature
// detection, never user-agent sniffing), and the deployment offers web push
// (capabilities: push enabled, the web platform, a VAPID public key).
// Subscribing registers /notify-sw.js at the site root, subscribes with the
// active credential's VAPID key and posts the subscription to /device with
// platform "web". Never while impersonating (R-IMP).
export const PushOptIn = () => {
  const supported = pushSupported()
  const access = useSelfNotifyAccess()
  const [caps, setCaps] = useState(undefined)
  const [state, setState] = useState('checking') // checking | off | on | blocked
  const [busy, setBusy] = useState(false)
  const [error, failure, setError] = useErrorLine()
  const live = useRef(true)
  const subscriptionRef = useRef(null)

  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
    }
  }, [])

  useEffect(() => {
    if (!supported) return
    getCapabilities().then(c => live.current && setCaps(c || null), () => live.current && setCaps(null))
  }, [supported])

  const vapid = caps && caps.push && caps.push.enabled && (caps.push.platforms || []).includes('web') ? caps.push.vapidPublicKey : null

  useEffect(() => {
    if (!supported || !vapid) return
    ;(async () => {
      if (window.Notification.permission === 'denied') {
        setState('blocked')
        return
      }
      try {
        const reg = await window.navigator.serviceWorker.getRegistration(SW_PATH)
        const sub = reg && reg.pushManager ? await reg.pushManager.getSubscription() : null
        if (!live.current) return
        subscriptionRef.current = sub
        setState(sub && window.Notification.permission === 'granted' ? 'on' : 'off')
      } catch (e) {
        if (live.current) setState('off')
      }
    })()
  }, [supported, vapid])

  if (!supported || !vapid) return null

  const turnOn = async () => {
    if (busy || !access.canWrite) return
    setBusy(true)
    setError(null)
    let sub = null
    try {
      const permission = await window.Notification.requestPermission()
      if (permission !== 'granted') {
        if (live.current) setState(permission === 'denied' ? 'blocked' : 'off')
        return
      }
      const reg = await window.navigator.serviceWorker.register(SW_PATH, { scope: '/' })
      sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(vapid) })
      const device = await registerDevice(subscriptionBody(sub, { deviceName: browserLabel(), locale: navigator.language || undefined, timezone: zone() }))
      rememberDevice(device && device.id ? { id: device.id, endpoint: sub.endpoint } : null)
      subscriptionRef.current = sub
      if (live.current) setState('on')
    } catch (err) {
      // The server refused (push off, impersonating): leave nothing behind.
      if (sub && typeof sub.unsubscribe === 'function') Promise.resolve().then(() => sub.unsubscribe()).catch(() => {})
      if (live.current) setError(serverMessage(err, FAILED), isOffline(err) || !err || !err.status ? null : err)
    } finally {
      if (live.current) setBusy(false)
    }
  }

  const turnOff = async () => {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      const sub = subscriptionRef.current
      if (sub && typeof sub.unsubscribe === 'function') await sub.unsubscribe()
      let id = (rememberedDevice() || {}).id
      if (!id) {
        const devices = (await listDevices()) || []
        const mine = devices.find(d => d.platform === 'web' && d.current)
        id = mine && mine.id
      }
      if (id) await deleteDevice(id)
      rememberDevice(null)
      subscriptionRef.current = null
      if (live.current) setState('off')
    } catch (err) {
      if (live.current) setError(serverMessage(err, OFF_FAILED), err && err.status ? err : null)
    } finally {
      if (live.current) setBusy(false)
    }
  }

  return (
    <FleetSection id="nt-push" title="Push in this browser">
      {state === 'checking' && <p className="sw-hint" role="status">Checking this browser…</p>}

      {state === 'off' &&
        <div>
          <p className="mb-2">Get a notification on this computer when something needs you, even when EducationXR isn't open. Your browser asks you to allow it first.</p>
          {caps.push.capture && <p className="sw-hint small">On this test environment, pushes are recorded for testing instead of being sent.</p>}
          {access.reason && <p className="sw-hint small">{access.reason}</p>}
          <button type="button" className="btn btn-sm sw-btn-primary" disabled={busy || !access.canWrite} onClick={turnOn}>Turn on push in this browser</button>
        </div>}

      {state === 'on' &&
        <div>
          <p className="mb-2">Push is on in this browser.</p>
          <button type="button" className="btn btn-sm sw-btn" disabled={busy} onClick={turnOff}>Turn off</button>
        </div>}

      {state === 'blocked' && <Notice tone="warn" role="status">{BLOCKED}</Notice>}

      {error && <p className="text-danger small mt-2" role="alert">{error}</p>}
      {error && <ErrorReference error={failure} className="mb-2" />}
    </FleetSection>
  )
}
